import React from 'react';

import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import Text from '~/components/core/TextComponents/Text';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import ReorderDragHandle from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/components/ReorderDragHandle';
import Switch from '~/components/ui/atoms/Switch';
import type { MoiVendorAssignmentRuleInfo } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';

import { TEXTS } from '../texts';

import { gridClassFor } from './MoiAssignmentRulesTableHeader';

interface MoiAssignmentRuleRowProps {
  rule: MoiVendorAssignmentRuleInfo;
  dragHandleProps: DndDragHandleProps;
  /** True while a write is in flight — the handle is then inert. */
  isDragDisabled: boolean;
  isProcessing: boolean;
  showSubOrgColumn: boolean;
  subOrgIdToName: Record<number, string>;
  lobConfigurationsDict: Record<string, unknown>;
  moiMethodNameByKey: Record<string, string>;
  /** The assignee cell's text. The table builds it: it holds the contact names and the role
   *  dictionary, which the row is not handed. */
  assigneeLabel: string;
  onEdit: (rule: MoiVendorAssignmentRuleInfo) => void;
  onDelete: (rule: MoiVendorAssignmentRuleInfo) => void | Promise<void>;
  onEnabledChanged: (rule: MoiVendorAssignmentRuleInfo) => void | Promise<void>;
}

const subOrgsLabel = (scope: MoiVendorAssignmentRuleInfo['scope'], subOrgIdToName: Record<number, string>): string =>
  scope.is_all_sub_orgs
    ? TEXTS.ALL
    : // A sub-organization the org no longer lists (removed since the rule was written) has no
      // name; fall back to its id so the cell still says the rule is scoped, rather than
      // dropping the entry and rendering blank — blank reads like "no scope" next to an "All"
      // row. Same fallback the LOB cell makes for an unknown LOB key.
      (scope.sub_organization_ids ?? []).map((id) => subOrgIdToName[id] ?? TEXTS.SUB_ORG_FALLBACK(id)).join(', ');

const lobsLabel = (
  scope: MoiVendorAssignmentRuleInfo['scope'],
  lobConfigurationsDict: Record<string, unknown>
): string =>
  scope.is_all_lobs
    ? TEXTS.ALL
    : // A LOB missing from the org config has no description; fall back to its key so the cell
      // names the LOB instead of rendering blank.
      (scope.lobs ?? []).map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict) ?? lobKey).join(', ');

const moiMethodsLabel = (
  moiMethods: MoiVendorAssignmentRuleInfo['moi_methods'],
  moiMethodNameByKey: Record<string, string>
): string =>
  moiMethods.is_all
    ? TEXTS.ALL
    : // A key with no name in the map (a method the organization no longer has) falls back to the
      // key itself, so the cell names the method instead of rendering blank.
      (moiMethods.values ?? []).map((key) => moiMethodNameByKey[key] ?? key).join(', ');

// `content` is a plain string, not a `ReactNode`: every cell below is a joined label, and `Text`
// declares `children` as `PropTypes.node.isRequired`, so a possibly-`undefined` `ReactNode` is not
// assignable to it.
const cell = (content: string, key: string) => (
  <Text key={key} variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR}>
    {content}
  </Text>
);

const MoiAssignmentRuleRow: React.FC<MoiAssignmentRuleRowProps> = ({
  rule,
  dragHandleProps,
  isDragDisabled,
  isProcessing,
  showSubOrgColumn,
  subOrgIdToName,
  lobConfigurationsDict,
  moiMethodNameByKey,
  assigneeLabel,
  onEdit,
  onDelete,
  onEnabledChanged,
}) => {
  const actionOptions = [
    { onClick: () => onEdit(rule), node: TEXTS.EDIT, key: 'editRule' },
    {
      onClick: () => onDelete(rule),
      withConfirmProps: {
        title: TEXTS.DELETE_TITLE,
        contentText: TEXTS.DELETE_TEXT(rule.name),
        primaryButtonName: TEXTS.DELETE,
      },
      node: TEXTS.DELETE,
      key: 'deleteRule',
    },
  ];

  return (
    <div
      className={cn(
        gridClassFor(showSubOrgColumn),
        'border-0 border-b border-solid border-slate-300 bg-white py-8',
        // A disabled rule stays in the priority list (the BE returns it) but reads as inactive.
        // Dimming the children rather than the row keeps the row's own background opaque, so a
        // disabled row still reads solid while being dragged over the list behind it.
        rule.is_enabled ? '' : '*:opacity-60'
      )}
    >
      <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={isDragDisabled} />
      {cell(rule.name, 'name')}
      {showSubOrgColumn ? cell(subOrgsLabel(rule.scope, subOrgIdToName), 'subOrgs') : null}
      {cell(lobsLabel(rule.scope, lobConfigurationsDict), 'lob')}
      {cell(moiMethodsLabel(rule.moi_methods, moiMethodNameByKey), 'moiMethods')}
      {cell(assigneeLabel, 'assignee')}
      {/* Named after the rule it belongs to: in a reorderable list a positional selector follows
          whichever row happens to sit at that index, which changes as soon as the order does. */}
      <Switch
        aria-label={TEXTS.ENABLE_RULE_ARIA_LABEL(rule.name)}
        onChange={() => onEnabledChanged(rule)}
        disabled={isProcessing}
        checked={rule.is_enabled}
      />
      <div>
        {/* Disabled alongside the switch above: Delete must not be clickable while a delete, enable
            or reorder write is still in flight. */}
        <ThreeDotsMenu options={actionOptions} disabled={isProcessing} />
      </div>
    </div>
  );
};

export default MoiAssignmentRuleRow;
